import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Train, Car, Mail } from 'lucide-react';
import WhatsAppIcon from '@/components/WhatsAppIcon';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import SiteFooter from '../components/SiteFooter';
import Breadcrumbs from '../components/Breadcrumbs';
import DirectAnswer from '../components/DirectAnswer';
import EnquiryForm from '../components/EnquiryForm';
import { Button } from '../components/ui/button';
import { contactPageSEO, PUBLIC_SITE_URL } from '../data/seo';
import { PRACTICE, WHATSAPP_LINK } from '../data/practice';
import { getDentistClinicLD, getContactPageLD, getBreadcrumbLD, getFAQPageLD } from '../data/structuredData';

const contactFAQs = [
  { question: 'Where is Restore.Dental located?', answer: 'Restore.Dental is at 64a Horn Lane, Acton, London W3 6NP — a 4-minute walk from Acton Main Line station (Elizabeth line) and 9 minutes from Acton Central (Overground).' },
  { question: 'What are your opening hours?', answer: 'We are open Monday to Friday 9:00am–6:00pm and Saturday 9:00am–2:00pm. Closed Sundays. Same-day emergency appointments are often available — WhatsApp or call 07787 814 064.' },
  { question: 'How do I book an appointment?', answer: 'Call or WhatsApp 07787 814 064, or use the Book Now button to see live availability. New patients receive a free consultation when they proceed with a treatment plan.' },
  { question: 'Is there parking near the practice?', answer: 'Yes — pay-and-display parking is available on Horn Lane and surrounding streets, and the Morrisons Acton car park is a 3-minute walk away.' },
];

const MAP_EMBED = 'https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d2481.950891234567!2d-0.27099170810255924!3d51.51103445602675!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zNTHCsDMwJzM5LjciTiAwwrAxNicxNS42Ilc!5e0!3m2!1sen!2suk!4v1709502123456!5m2!1sen!2suk';

export default function Contact() {
  const crumbs = [{ name: 'Home', to: '/' }, { name: 'Contact', to: '/contact' }];
  return (
    <div className="min-h-screen bg-white">
      <SEO
        {...contactPageSEO}
        structuredData={[
          getDentistClinicLD(),
          getContactPageLD(),
          getBreadcrumbLD(crumbs.map((c) => ({ name: c.name, url: `${PUBLIC_SITE_URL}${c.to}` }))),
          getFAQPageLD(contactFAQs),
        ]}
      />
      <PageHeader />
      <main className="pt-28 md:pt-32">
        <section className="container mx-auto px-4 max-w-5xl">
          <Breadcrumbs items={crumbs} />
          <p className="text-sm font-semibold uppercase tracking-widest text-[#006A71] mt-6 mb-3">Contact & directions</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-rabbit leading-tight mb-6">Contact Restore.Dental, Acton</h1>
          <DirectAnswer
            answer={`Restore.Dental is at ${PRACTICE.addressOneLine}, 4 minutes' walk from Acton Main Line (Elizabeth line). Call or WhatsApp ${PRACTICE.phoneDisplay}. Open Monday–Friday 9am–6pm and Saturday 9am–2pm, with same-day emergency slots often available.`}
          />

          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <div className="space-y-6" itemScope itemType="https://schema.org/Dentist">
              <meta itemProp="name" content={PRACTICE.name} />
              <div className="flex gap-4 p-5 rounded-xl border border-sky-gray/20">
                <MapPin className="w-6 h-6 text-burnt-coral flex-shrink-0" aria-hidden="true" />
                <div>
                  <h2 className="font-bold text-rabbit mb-1">Address</h2>
                  <address className="not-italic text-chocolate-malt" itemProp="address" itemScope itemType="https://schema.org/PostalAddress" data-testid="contact-address">
                    <span itemProp="streetAddress">{PRACTICE.address.street}</span><br />
                    <span itemProp="addressLocality">{PRACTICE.address.locality}</span>, <span itemProp="addressRegion">{PRACTICE.address.region}</span> <span itemProp="postalCode">{PRACTICE.address.postcode}</span>
                  </address>
                  <a href={PRACTICE.links.googleMaps} target="_blank" rel="noopener noreferrer" className="text-sm text-[#006A71] underline underline-offset-4 mt-2 inline-block" data-testid="contact-directions-link">Get directions on Google Maps</a>
                </div>
              </div>

              <div className="flex gap-4 p-5 rounded-xl border border-sky-gray/20">
                <Phone className="w-6 h-6 text-burnt-coral flex-shrink-0" aria-hidden="true" />
                <div>
                  <h2 className="font-bold text-rabbit mb-1">Phone & WhatsApp</h2>
                  <a href={PRACTICE.phoneTel} className="text-lg font-semibold text-rabbit hover:text-burnt-coral" itemProp="telephone" data-testid="contact-phone-link">{PRACTICE.phoneDisplay}</a>
                  <p className="text-sm text-chocolate-malt mt-1">Message us on WhatsApp for the fastest reply — photos welcome.</p>
                  <div className="flex flex-wrap gap-3 mt-3">
                    <Button asChild size="sm" className="bg-[#25D366] hover:bg-[#20BA5A] text-white"><a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn"><WhatsAppIcon className="w-4 h-4 mr-2" />WhatsApp</a></Button>
                    <Button asChild size="sm" variant="outline"><a href={`mailto:${PRACTICE.email}`} data-testid="contact-email-link"><Mail className="w-4 h-4 mr-2" />{PRACTICE.email}</a></Button>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 p-5 rounded-xl border border-sky-gray/20">
                <Clock className="w-6 h-6 text-burnt-coral flex-shrink-0" aria-hidden="true" />
                <div className="w-full">
                  <h2 className="font-bold text-rabbit mb-2">Opening hours</h2>
                  <table className="w-full text-sm text-chocolate-malt" data-testid="opening-hours-table">
                    <tbody>
                      {PRACTICE.openingHours.map((h) => (
                        <tr key={h.label}><th scope="row" className="text-left font-medium py-1">{h.label}</th><td className="text-right py-1"><time>{h.opens}</time> – <time>{h.closes}</time></td></tr>
                      ))}
                      <tr><th scope="row" className="text-left font-medium py-1">Sunday</th><td className="text-right py-1">Closed</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex gap-4 p-5 rounded-xl border border-sky-gray/20">
                <Train className="w-6 h-6 text-burnt-coral flex-shrink-0" aria-hidden="true" />
                <div>
                  <h2 className="font-bold text-rabbit mb-2">Getting here by train & tube</h2>
                  <ul className="text-sm text-chocolate-malt space-y-1">
                    {PRACTICE.transport.map((t) => <li key={t.name}><strong>{t.name}</strong> ({t.lines}) — {t.walk}</li>)}
                  </ul>
                  <p className="text-sm text-chocolate-malt mt-3 flex gap-2"><Car className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />{PRACTICE.parking}</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden shadow-xl border-4 border-sky-gray/20 min-h-[420px]">
              <iframe
                src={MAP_EMBED}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: 420 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Map showing Restore.Dental at 64a Horn Lane, Acton W3 6NP"
                allowFullScreen=""
              ></iframe>
            </div>
          </div>
        </section>

        <section className="container mx-auto px-4 max-w-5xl mt-16" id="enquiry">
          <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-2">Send us an enquiry</h2>
          <p className="text-chocolate-malt mb-6">Tell us what you need and we'll reply within one working day — by email, phone or WhatsApp.</p>
          <EnquiryForm source="/contact" />
        </section>

        <section className="bg-sky-gray/10 py-16 mt-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-8">Visiting us — common questions</h2>
            <div className="space-y-6">
              {contactFAQs.map((f) => (
                <div key={f.question} className="border-b border-sky-gray/20 pb-6">
                  <h3 className="text-lg font-bold text-rabbit mb-2">{f.question}</h3>
                  <p className="text-chocolate-malt leading-relaxed">{f.answer}</p>
                </div>
              ))}
            </div>
            <p className="mt-10 text-chocolate-malt">Travelling from further afield? See our guides for patients from <Link to="/dentist-ealing" className="underline underline-offset-4">Ealing</Link>, <Link to="/dentist-chiswick" className="underline underline-offset-4">Chiswick</Link>, <Link to="/dentist-hammersmith" className="underline underline-offset-4">Hammersmith</Link> and <Link to="/dentist-shepherds-bush" className="underline underline-offset-4">Shepherd&apos;s Bush</Link>.</p>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
